import React, { createContext, useContext, useEffect, useState } from 'react';
import { authApi } from '../services/api/authApi';
import { userApi } from '../services/api/userApi';

interface User {
  _id?: string;
  id?: string;
  email: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  gender?: string;
  dateOfBirth?: string;
  role: string;
  kycVerified?: boolean;
  isCartShared?: boolean;
  // Add other user properties as needed
}

interface AuthContextType {
  isLoggedIn: boolean;
  isSignUp: boolean;
  user: User | null;
  login: (token: string, user: User) => void;
  signup: (user: User) => void;
  logout: () => Promise<void>;
  updateUser: (user: User) => void;
  refreshUserData: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isSignUp, setIsSignUp] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const checkAuthStatus = async () => {
      const token = localStorage.getItem('accessToken');
      const userData = localStorage.getItem('user');

      if (token && userData) {
        try {
          // Fetch fresh user data from server to reflect any admin updates
          const response = await userApi.getProfile();
          const freshUserData = response.data;
          
          // Update localStorage with fresh data
          localStorage.setItem('user', JSON.stringify(freshUserData));
          
          setIsLoggedIn(true);
          setIsSignUp(false);
          setUser(freshUserData);
        } catch (error) {
          console.error('Error fetching fresh user data:', error);
          // Fall back to cached data if server request fails
          setIsLoggedIn(true);
          setIsSignUp(false);
          setUser(JSON.parse(userData));
        }
      } else if (userData && !token) {
        setIsSignUp(true);
        setIsLoggedIn(false);
        setUser(JSON.parse(userData));
      } else {
        setIsLoggedIn(false);
        setIsSignUp(false);
        setUser(null);
      }
      setIsLoading(false);
    };

    checkAuthStatus();
  }, []);

  const signup = (userData: User) => {
    localStorage.setItem('user', JSON.stringify(userData));
    setIsLoggedIn(false);
    setIsSignUp(true);
    setUser(userData);
  };

  const login = (token: string, userData: User) => {
    localStorage.setItem('accessToken', token);
    localStorage.setItem('user', JSON.stringify(userData));
    setIsLoggedIn(true);
    setIsSignUp(false);
    setUser(userData);
  };

  const logout = async () => {
    try {
      await authApi.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('accessToken');
      localStorage.removeItem('user');
      setIsLoggedIn(false);
      setIsSignUp(false);
      setUser(null);
    }
  };

  const updateUser = (userData: User) => {
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
  };

  const refreshUserData = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      if (token) {
        const response = await userApi.getProfile();
        const freshUserData = response.data;
        localStorage.setItem('user', JSON.stringify(freshUserData));
        setUser(freshUserData);
      }
    } catch (error) {
      console.error('Error refreshing user data:', error);
    }
  };

  if (isLoading) {
    return <div>Loading...</div>;
  }

  return (
    <AuthContext.Provider value={{ isLoggedIn, isSignUp, user, login, signup, logout, updateUser, refreshUserData }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
